// src/utils/logger.js
// 🧹 SMART LOGGING SYSTEM — clean console while keeping functionality

// ---------------------------------------------------------------------------
// Environment detection (defensive)
// ---------------------------------------------------------------------------
const MODE = process.env.NODE_ENV || 'production';
const isDevelopment = MODE === 'development';

// ---------------------------------------------------------------------------
// LocalStorage-backed flags (defensive reads)
// ---------------------------------------------------------------------------
const isDebugMode = () => {
  try { return localStorage.getItem('debugMode') === 'true'; } catch { return false; }
};
const isTraceMode = () => {
  try { return localStorage.getItem('traceMode') === 'true'; } catch { return false; }
};

// ---------------------------------------------------------------------------
// Log levels
// ---------------------------------------------------------------------------
const LOG_LEVELS = {
  ERROR: 0, // always show
  WARN:  1,
  INFO:  2,
  DEBUG: 3,
  TRACE: 4,
};

const LEVEL_NAMES = Object.keys(LOG_LEVELS).reduce((acc, k) => { acc[LOG_LEVELS[k]] = k; return acc; }, {});

// Current level
const getCurrentLogLevel = () => {
  if (!isDevelopment) return LOG_LEVELS.ERROR; // production: only errors
  if (isTraceMode())  return LOG_LEVELS.TRACE;
  if (isDebugMode())  return LOG_LEVELS.DEBUG;
  return LOG_LEVELS.INFO; // dev default
};

// Core gated logger
const logWithLevel = (level, emoji, consoleFn, ...args) => {
  if (getCurrentLogLevel() >= level) {
    try { consoleFn(emoji, ...args); } catch { /* noop */ }
  }
};

// ---------------------------------------------------------------------------
// Public logger API
// ---------------------------------------------------------------------------
export const logger = {
  // Core
  error: (...args) => { try { console.error('❌', ...args); } catch {} },
  warn:  (...args) => logWithLevel(LOG_LEVELS.WARN,  '⚠️', console.warn, ...args),
  info:  (...args) => logWithLevel(LOG_LEVELS.INFO,  'ℹ️', console.log,  ...args),
  success:(...args) => logWithLevel(LOG_LEVELS.INFO,  '✅', console.log,  ...args),
  security:(...args)=> logWithLevel(LOG_LEVELS.WARN,  '🚨', console.warn, ...args),

  // Debug & Trace
  debug: (...args) => logWithLevel(LOG_LEVELS.DEBUG, '🐛', console.log, ...args),
  trace: (...args) => logWithLevel(LOG_LEVELS.TRACE, '🔍', console.log, ...args),

  // Category channels (aligned with app)
  firebase:   (...args) => logWithLevel(LOG_LEVELS.DEBUG, '🔥', console.log, ...args),
  completion: (...args) => logWithLevel(LOG_LEVELS.DEBUG, '🎯', console.log, ...args),
  credits:    (...args) => logWithLevel(LOG_LEVELS.DEBUG, '💳', console.log, ...args),
  auth:       (...args) => logWithLevel(LOG_LEVELS.DEBUG, '👤', console.log, ...args),
  navigation: (...args) => logWithLevel(LOG_LEVELS.DEBUG, '📍', console.log, ...args),
  game:       (...args) => logWithLevel(LOG_LEVELS.DEBUG, '🎮', console.log, ...args),
  sports:     (...args) => logWithLevel(LOG_LEVELS.DEBUG, '🏈', console.log, ...args),
  database:   (...args) => logWithLevel(LOG_LEVELS.DEBUG, '💾', console.log, ...args),

  // High-volume → TRACE level
  performance: (...args) => {
    if (typeof window !== 'undefined' && window.__mutePerformance) return;
    logWithLevel(LOG_LEVELS.TRACE, '⚡', console.log, ...args);
  },
  network:   (...args) => logWithLevel(LOG_LEVELS.TRACE, '🌐', console.log, ...args),
  component: (...args) => logWithLevel(LOG_LEVELS.TRACE, '🔄', console.log, ...args),
};

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------
// FIX: Consolidate redundant logic and provide an accurate log message.
const resetToDefault = () => {
    try {
      localStorage.setItem('debugMode', 'false');
      localStorage.setItem('traceMode', 'false');
    } catch {}
    logger.info('Log level reset to default (INFO in dev, errors only in prod)');
};

export const logControl = {
  enableInfo: resetToDefault, // Alias for resetting
  enableDebug: () => {
    try { localStorage.setItem('debugMode', 'true');  localStorage.setItem('traceMode', 'false'); } catch {}
    logger.info('Log level set to DEBUG');
  },
  enableTrace: () => {
    try { localStorage.setItem('debugMode', 'true');  localStorage.setItem('traceMode', 'true'); } catch {}
    logger.info('Log level set to TRACE (all logs)');
  },
  disableAll: resetToDefault, // Alias for resetting

  // Category toggles
  mutePerformance: () => { if (typeof window !== 'undefined') window.__mutePerformance = true;  logger.info('Performance logs muted'); },
  unmutePerformance: () => { if (typeof window !== 'undefined') window.__mutePerformance = false; logger.info('Performance logs enabled'); },

  // Snapshot
  getStatus: () => {
    const level = getCurrentLogLevel();
    return {
      level,
      levelName: LEVEL_NAMES[level] || 'UNKNOWN',
      isDevelopment,
      isDebugMode: isDebugMode(),
      isTraceMode: isTraceMode(),
      performanceMuted: typeof window !== 'undefined' ? !!window.__mutePerformance : false,
    };
  },
};

// ---------------------------------------------------------------------------
// Migration Helper
// ---------------------------------------------------------------------------
export const migrationHelper = {
  findOldLogs: () => {
    if (!isDevelopment) return;
    console.group('🔍 Migration Helper — Replace raw console logs');
    console.log("console.log('🎯' ...) → logger.completion(...)");
    console.log("console.log('🔥' ...) → logger.firebase(...)");
    console.log("console.log('💳' ...) → logger.credits(...)");
    console.log("console.log('👤' ...) → logger.auth(...)");
    console.log("console.log('🐛' ...) → logger.debug(...)");
    console.log("console.log('ℹ️' ...) → logger.info(...)");
    console.log("console.log('✅' ...) → logger.success(...)");
    console.log("console.log('📍' ...) → logger.navigation(...)");
    console.log("console.log('🏈' ...) → logger.sports(...)");
    console.log("console.log('💾' ...) → logger.database(...)");
    console.groupEnd();
  },

  testAllLoggers: () => {
    if (!isDevelopment) return;
    console.group('🧪 Logger Channel Test');
    logger.error('Error message (always)');
    logger.warn('Warning message');
    logger.info('Info message');
    logger.success('Success message');
    logger.debug('Debug message');
    logger.trace('Trace message');
    logger.firebase('Firebase message');
    logger.completion('Completion message');
    logger.credits('Credits message');
    logger.auth('Auth message');
    logger.navigation('Navigation message');
    logger.game('Game message');
    logger.sports('Sports message');
    logger.performance('Performance message');
    logger.network('Network message');
    logger.database('Database message');
    logger.component('Component message');
    console.groupEnd();
  },
};

// ---------------------------------------------------------------------------
// Production console sanitizer (idempotent & safe)
// ---------------------------------------------------------------------------
let __consolePatched = false;
export const replaceConsoleLogs = () => {
  if (__consolePatched) return;
  if (MODE !== 'production') return;

  try {
    const originalLog  = console.log.bind(console);
    const originalWarn = console.warn.bind(console);

    console.log = (...args) => {
      const first = String(args[0] ?? '');
      if (first.includes('❌') || first.includes('⚠️') || first.includes('🚨')) {
        originalLog(...args);
      }
    };

    console.warn = (...args) => {
      const first = String(args[0] ?? '');
      if (first.includes('❌') || first.includes('⚠️') || first.includes('🚨')) {
        originalWarn(...args);
      }
    };

    __consolePatched = true;
  } catch {
    // If patch fails, leave console intact
  }
};

// ---------------------------------------------------------------------------
// Performance helpers
// ---------------------------------------------------------------------------
export const perfLogger = {
  time: (label) => {
    if (getCurrentLogLevel() >= LOG_LEVELS.TRACE && !(typeof window !== 'undefined' && window.__mutePerformance)) {
      try { console.time(`⚡ ${label}`); } catch {}
    }
  },
  timeEnd: (label) => {
    if (getCurrentLogLevel() >= LOG_LEVELS.TRACE && !(typeof window !== 'undefined' && window.__mutePerformance)) {
      try { console.timeEnd(`⚡ ${label}`); } catch {}
    }
  },
  mark: (label, data = null) => {
    if (getCurrentLogLevel() >= LOG_LEVELS.TRACE && !(typeof window !== 'undefined' && window.__mutePerformance)) {
      const ts = (typeof performance !== 'undefined' && performance.now) ? performance.now().toFixed(2) : 'n/a';
      logger.performance(`${label} (${ts}ms)`, data);
    }
  },
};

// ---------------------------------------------------------------------------
// Dev globals (convenience)
// ---------------------------------------------------------------------------
if (isDevelopment && typeof window !== 'undefined') {
  window.logControl = logControl;
  window.logger = logger;
  window.migrationHelper = migrationHelper;

  // quick shortcuts
  window.info = logControl.enableInfo; // FIX: Add shortcut for info
  window.debug = logControl.enableDebug;
  window.trace = logControl.enableTrace;
  window.quiet = logControl.disableAll;
  window.logStatus = logControl.getStatus;

  if (getCurrentLogLevel() >= LOG_LEVELS.INFO) {
    console.log('🔧 Logger initialized. Use window.info(), .debug(), .trace(), or .quiet() to control logs.');
  }
}

// ---------------------------------------------------------------------------
// Default export
// ---------------------------------------------------------------------------
export default {
  logger,
  logControl,
  migrationHelper,
  perfLogger,
  replaceConsoleLogs,
};